/**
 * Branding utility helpers for white-label support.
 *
 * D4-WLB: The default AGENCY_NAME is "CRM". Ten call sites append " CRM" to the
 * agency name, producing "CRM CRM" on unconfigured instances. This helper
 * conditionally appends " CRM" only when the name does not already end with it.
 */

/**
 * Return the agency name with " CRM" appended, unless it already ends with
 * "CRM" (case-insensitive). Empty/whitespace-only input returns "CRM".
 *
 * Examples:
 *   agencyCrmLabel("LWP")       -> "LWP CRM"
 *   agencyCrmLabel("CRM")       -> "CRM"
 *   agencyCrmLabel("Acme CRM")  -> "Acme CRM"
 *   agencyCrmLabel("Acme crm")  -> "Acme crm"
 *   agencyCrmLabel("")           -> "CRM"
 */
export function agencyCrmLabel(name: string): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "CRM";
  if (trimmed.toLowerCase().endsWith("crm")) return trimmed;
  return `${trimmed} CRM`;
}

/**
 * The neutral, unconfigured agency name (see `DEFAULT_BRANDING.agency_name` and
 * the `AGENCY_NAME` env default). Treated as "no agency name configured".
 */
const NEUTRAL_AGENCY_NAME = "crm";

/**
 * How to refer to the agency in user-facing PROSE (D4-WLB-009).
 *
 * Copy that names the agency ("still to be confirmed with LWP") must never
 * hardcode one deployment's brand. Drive it from `useBranding().agency_name`
 * instead — and, when that is blank or still the neutral default, fall back to
 * the generic phrase rather than the literal word "CRM", which reads as
 * nonsense mid-sentence ("confirmed with CRM"). A branding-fetch failure
 * therefore degrades to neutral prose and can never leak another agency.
 *
 * Examples:
 *   agencyReference("LWP")        -> "LWP"
 *   agencyReference("Acme Realty") -> "Acme Realty"
 *   agencyReference("CRM")        -> "the agency"
 *   agencyReference("")            -> "the agency"
 */
export function agencyReference(name: string | null | undefined): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed || trimmed.toLowerCase() === NEUTRAL_AGENCY_NAME) {
    return "the agency";
  }
  return trimmed;
}

/**
 * Possessive form of {@link agencyReference} — "LWP's" / "the agency's".
 *
 * Uses a typographic apostrophe (U+2019) to match the surrounding CRM copy.
 */
export function agencyPossessive(name: string | null | undefined): string {
  return `${agencyReference(name)}’s`;
}

/**
 * The agency's iOS home-screen icon (`<link rel="apple-touch-icon">`), shared
 * by the CRM (BrandingProvider) and the client portal (usePortalDocumentChrome,
 * D9-WLB-004) so both surfaces show the same mark (#3730).
 *
 * The square compact logo wins — it is built for app chrome and survives iOS's
 * 180px rendering; a favicon is typically a 16/32px `.ico`, so it is only the
 * fallback. `null` means "no agency icon": keep the platform default.
 */
export function agencyTouchIconUrl(branding: {
  logo_compact_url?: string | null;
  favicon_url?: string | null;
}): string | null {
  return branding.logo_compact_url || branding.favicon_url || null;
}
